<style>
.date-time-picker .v-input--is-disabled .v-input__slot::before {
  border-image: none !important;
  border-bottom-style: solid !important;
  border-bottom-width: 1px !important;
  border-bottom-color: rgba(0, 0, 0, 0.12) !important;
}
</style>

<template>
  <v-container class="d-flex pa-0 date-time-picker" style="gap: 20px">
    <v-menu
      v-model="isOpen"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      offset-overflow
      max-width="290px"
      min-width="auto"
    >
      <template #activator="{ on, attrs }">
        <v-text-field
          v-bind="attrs"
          v-on="on"
          v-model="dateText"
          hide-details="auto"
          :prepend-icon="icon"
          :label="dateLabel"
          placeholder="YYYY/MM/DD"
          maxlength="12"
          :disabled="disabled"
          :error="hasPeriodError"
          :error-messages="dateError"
          @input="onDateInput"
          @blur="onDateBlur"
        />
      </template>
      <v-date-picker
        v-model="localDate"
        no-title
        @change="isOpen = false"
        @input="onDatePicked"
      />
    </v-menu>
    <v-combobox
      v-model="localTime"
      :items="timeOptions"
      hide-details="auto"
      :label="timeLabel"
      :placeholder="timeLabel || 'Time'"
      :disabled="disabled"
      maxlength="7"
      :error="hasPeriodError"
      :error-messages="timeError"
      @change="emitTime"
    />
  </v-container>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "@vue/composition-api";
import {
  parseDateText,
  parseTimeText,
  TIME_SLOTS,
  toDisplayDate,
} from "@/utils/dateUtils";

export default defineComponent({
  name: "DatePicker",

  props: {
    date: {
      type: String as PropType<string | null>,
      default: null,
    },
    time: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    dateLabel: {
      type: String as PropType<string | undefined>,
      default: "",
    },
    timeLabel: {
      type: String as PropType<string | undefined>,
      default: "",
    },
    hasPeriodError: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      timeOptions: TIME_SLOTS,
      localDate: this.date,
      localTime: this.time,
      dateText: toDisplayDate(this.date),
      isOpen: false,
    };
  },

  watch: {
    date(value: string | null) {
      this.localDate = value;
      // Skip when the parent echoes our own emit back, so in-progress typing is not wiped
      if (parseDateText(this.dateText) !== value) {
        this.dateText = toDisplayDate(value);
      }
    },
    time(value: string) {
      // Keep invalid text visible when the parent echoes back the empty value we emitted
      if (
        value === "" &&
        this.localTime &&
        parseTimeText(this.localTime) === null
      ) {
        return;
      }
      this.localTime = value;
    },
  },

  computed: {
    dateError(): string {
      if (!this.dateText || parseDateText(this.dateText)) return "";
      return this.$t("validation.dateAndTime.invalidDateError") as string;
    },

    timeError(): string {
      if (!this.localTime || parseTimeText(this.localTime)) return "";
      return this.$t("validation.dateAndTime.invalidTimeError") as string;
    },
  },

  methods: {
    onDateInput() {
      this.localDate = parseDateText(this.dateText);
      this.$emit("update:date", this.localDate);
      this.$emit("validate-period");
    },

    onDateBlur() {
      if (this.localDate) this.dateText = toDisplayDate(this.localDate);
    },

    onDatePicked() {
      this.dateText = toDisplayDate(this.localDate);
      this.onDateInput();
    },

    emitTime() {
      const parsed = parseTimeText(this.localTime || "");
      if (parsed) this.localTime = parsed;
      this.$emit("update:time", parsed ?? "");
      this.$emit("validate-period");
    },
  },
});
</script>
